import { computed, observable } from "mobx";
import { Type } from "class-transformer";
import SystemComponentOutageLocalizationRequest from "./systemComponentOutageLocalizationRequest";
import type { EntityValidationRules } from "../services/validationService";
import LanguageCode from "./languageCode";
import { getCustomOutage, setCustomOutage } from "../utils/customOutage";
import SystemComponentCode from "./systemComponentCode";
import { isTimeFormat } from "utils/helpers";

// This entity has been generated, do not change its content, your changes might get lost. You CAN modify the rest of the file.
class SystemComponentOutageRequest {
  @observable
  @Type(() => Date)
  validSince!: Date;

  @observable
  _validSinceTime?: string;

  @observable
  @Type(() => Date)
  validUntil?: Date;

  @observable
  _validUntilTime?: string;

  @observable
  @Type(() => Date)
  visibleSince!: Date;

  @observable
  _visibleSinceTime?: string;

  @observable
  @Type(() => Date)
  visibleUntil?: Date;

  @observable
  _visibleUntilTime?: string;

  @observable
  systemComponentId!: string;

  @observable
  _systemComponentCode?: string;

  @observable
  description?: string;

  @observable
  @Type(() => SystemComponentOutageLocalizationRequest)
  systemComponentOutageLocalizations?: SystemComponentOutageLocalizationRequest[];
}

export class SystemComponentOutage extends SystemComponentOutageRequest {
  @computed
  get customOutageCs() {
    return getCustomOutage({ languageCode: LanguageCode.Cs, localizations: this.systemComponentOutageLocalizations });
  }

  set customOutageCs(description: string) {
    this.systemComponentOutageLocalizations = setCustomOutage({
      description,
      languageCode: LanguageCode.Cs,
      localizations: this.systemComponentOutageLocalizations,
    });
  }

  @computed
  get customOutageEn() {
    return getCustomOutage({ languageCode: LanguageCode.En, localizations: this.systemComponentOutageLocalizations });
  }

  set customOutageEn(description: string) {
    this.systemComponentOutageLocalizations = setCustomOutage({
      description,
      languageCode: LanguageCode.En,
      localizations: this.systemComponentOutageLocalizations,
    });
  }
}

export const OutageValidationRules: EntityValidationRules<SystemComponentOutage> = {
  systemComponentId: {
    required: true,
  },
  validSince: {
    required: true,
  },

  validUntil: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) => !!outage._validUntilTime,
    },
    isAfter: {
      condition: (outage: SystemComponentOutage) => outage.validSince,
    },
  },
  _validUntilTime: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) =>
        !!outage.validUntil || (!!outage._validUntilTime && isTimeFormat(outage._validUntilTime)),
    },
  },
  _validSinceTime: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) =>
        !!outage.validSince || (!!outage._validSinceTime && !isTimeFormat(outage._validSinceTime)),
    },
    required: true,
  },
  _visibleSinceTime: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) =>
        !!outage.visibleSince && !!outage._visibleSinceTime && isTimeFormat(outage._visibleSinceTime),
    },
    required: true,
  },
  visibleSince: {
    required: true,
    isBefore: {
      condition: (outage: SystemComponentOutage) => outage.validSince,
      customMessage: "beforeValidSince",
    },
  },

  description: {
    isLength: {
      max: 500,
    },
  },
  customOutageCs: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) =>
        outage._systemComponentCode === SystemComponentCode.OTHER && outage.customOutageCs.length <= 0,
    },
    isLength: {
      max: 500,
    },
  },
  customOutageEn: {
    requiredIf: {
      condition: (outage: SystemComponentOutage) =>
        outage._systemComponentCode === SystemComponentCode.OTHER && outage.customOutageEn.length <= 0,
    },
    isLength: {
      max: 500,
    },
  },
};
